/**
 * Format picker modal (9 UGC presets, 5x2 grid).
 */
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import type { UgcFormat, UgcFormatId } from "./api";

interface FormatPickerModalProps {
  open: boolean;
  onClose: () => void;
  formats: UgcFormat[];
  selectedId: UgcFormatId;
  onSelect: (id: UgcFormatId) => void;
}

export function FormatPickerModal({
  open,
  onClose,
  formats,
  selectedId,
  onSelect,
}: FormatPickerModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="bg-[#0a0c10] border-white/[0.08] max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-white/80 text-sm tracking-wide uppercase">
            {t("ugcStudio.formatPicker.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2">
          {formats.map((fmt) => {
            const isActive = selectedId === fmt.id;
            return (
              <button
                key={fmt.id}
                onClick={() => {
                  onSelect(fmt.id);
                  onClose();
                }}
                className={`relative flex flex-col rounded-xl border text-left overflow-hidden transition-all ${
                  isActive
                    ? "border-lime-400 ring-2 ring-lime-400/40"
                    : "border-white/[0.08] hover:border-white/25"
                }`}
              >
                <div className="aspect-[3/4] bg-gradient-to-br from-white/[0.04] to-white/[0.01] flex items-center justify-center">
                  <span className="text-4xl">🎬</span>
                </div>
                <div className="p-3 bg-white/[0.02]">
                  <div className="text-white text-sm font-semibold">{fmt.label}</div>
                  <div className="text-white/50 text-xs line-clamp-2 mt-0.5">{fmt.description}</div>
                </div>
              </button>
            );
          })}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-3 right-3 text-white/50"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </Button>
      </DialogContent>
    </Dialog>
  );
}
